import React, { useCallback, useEffect, useRef, useState } from "react";

import { Box, CircularProgress, Divider, IconButton, Modal, Fade, Backdrop, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import {
  SenderLabel,
  SenderValue,
  StyledBox,
  StyledBoxFlex,
  StyledBoxStatus,
  StyledCardHeader,
  StyledHeaderRight,
  StyledImageContainer,
  StyledNoImageBox,
  StyledNoImageText,
  StyledLockerActionBox,
  StyledMainBox,
  StyledChipRow,
  StyledNoteButton,
  StyledNoteModalBox,
  StyledNoteModalContent,
  StyledNoteModalHeader,
  StyledNoteModalTitle,
  StyledNoteText,
  StyledOrderDetailsBox,
  StyledQuantity,
  StyledRowFlex,
  StyledSpan,
  StyledTitle,
  StyledInvoiceUploadBox,
  StyledInvoiceFileName,
  StyledInvoiceHint,
  StyledInvoiceModalDesc,
  StyledInvoiceButtonRow,
  StyledInvoiceCancelButton,
  StyledInvoiceSubmitButton,
} from "./style";
import CustomButton from "../common/button/CustomButton";
import CustomChip from "../common/chip/chip";
import theme from "../../theme";
import rectangle from "../../assets/images/rectangle-round.png";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { VIRTUAL_LOCKER_DELETE_ITEM, VIRTUAL_LOCKER_ITEM_STATUS_CHANGE } from "../../actions/VirtualLockerAction";
import ReviewApprove from "../modals/review-approve/ReviewApprove";
import ViewLockerItemModal from "../modals/view-locker-item/ViewLockerItemModal";
import AddNotesModal from "../modals/add-notes/AddNotesModal";
import {
  isAwaitingPictureReview,
  isLockerItemDeleteDisabled,
  isLockerItemEditDisabled,
  formatName,
} from "../../utils/common";
import { Select, MenuItem as MuiMenuItem } from "@mui/material";
import toast from "react-hot-toast";
import { API } from "../../api";

const statusConfig = {
  pending:          { bg: "#FFF3CD", color: "#856404", dot: "#F0A500" },
  review:           { bg: "#E8F4FF", color: "#0B65B5", dot: "#0B65B5" },
  wait_to_deliver:  { bg: "#FFF0E0", color: "#CC5500", dot: "#FF8500" },
  un_delivered:     { bg: "#FFE8E0", color: "#CC2200", dot: "#FF4500" },
  recieved:         { bg: "#E0F0FF", color: "#0B4A8C", dot: "#126ABC" },
  delivered:        { bg: "#E0F7EC", color: "#065F2E", dot: "#00C046" },
  approved:         { bg: "#E0F7EC", color: "#065F2E", dot: "#00C046" },
  rejected:         { bg: "#FFE8EA", color: "#9B0012", dot: "#FF3850" },
  awaiting_approval:{ bg: "#FFF0E0", color: "#7A3D00", dot: "#E17B0B" },
  item_delivered:   { bg: "#E0F7EC", color: "#065F2E", dot: "#00C046" },
  shopping_completed: { bg: "#E0F7EC", color: "#065F2E", dot: "#00C046" },
};

const CustomLocker = ({
  itemId,
  chip2Title,
  chip1Title,
  product,
  createdAt,
  expDelivery,
  senderType,
  orderType,
  preferredDropDate,
  images,
  quantity,
  createUser,
  status,
  getItemLocker,
  ind,
  note,
  customerDeclineNote,
  lockerView,
  lockerStatus,
  paymentStatus,
}) => {
  const path = useLocation();
  const isLockerV2 =
    lockerView === "v2" || path?.pathname === "/dashboard/locker-v2";
  const isPictureReviewPending = isAwaitingPictureReview(status) || (
    images?.length > 0 &&
    !['approved', 'rejected'].includes(String(status || '').toLowerCase())
  );
  const isLockerInactive = lockerStatus === "inactive" || lockerStatus === "expired";
  const hideEdit = isLockerItemEditDisabled(status) || isLockerInactive;
  const hideDelete = isLockerItemDeleteDisabled(status);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openReviewApprove, setOpenReviewApprove] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("");
  const [localPaymentStatus, setLocalPaymentStatus] = useState(paymentStatus || "");
  const [paymentUpdating, setPaymentUpdating] = useState(false);
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [pendingPaymentStatus, setPendingPaymentStatus] = useState("");
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceUploading, setInvoiceUploading] = useState(false);

  useEffect(() => {
    setLocalPaymentStatus(paymentStatus || "");
  }, [paymentStatus]);

  const isPersonalShopper = orderType === "personal_shopper";
  const isPaid = localPaymentStatus === "paid";
  const isRejected = String(status || "").toLowerCase() === "rejected";

  const handlePaymentStatusChange = (e) => {
    const newStatus = e.target.value;
    if (newStatus === "paid" || newStatus === "partial_paid") {
      setPendingPaymentStatus(newStatus);
      setInvoiceFile(null);
      setInvoiceModal(true);
      return;
    }
    setPaymentUpdating(true);
    dispatch({
      type: VIRTUAL_LOCKER_ITEM_STATUS_CHANGE,
      payload: {
        id: itemId,
        payment_status: newStatus,
        onSuccess: () => {
          setLocalPaymentStatus(newStatus);
          setPaymentUpdating(false);
          getItemLocker({});
        },
        onError: () => {
          setPaymentUpdating(false);
          toast.error("Failed to update payment status");
        },
      },
    });
  };

  const handleInvoiceSubmit = async () => {
    if (!invoiceFile) {
      toast.error("Please upload invoice / payment proof.");
      return;
    }
    setInvoiceUploading(true);
    try {
      const res = await API.uploadImage(invoiceFile);
      const url = res?.data?.url;
      if (!url) throw new Error("Upload failed");
      setPaymentUpdating(true);
      dispatch({
        type: VIRTUAL_LOCKER_ITEM_STATUS_CHANGE,
        payload: {
          id: itemId,
          payment_status: pendingPaymentStatus,
          invoice_of_product: url,
          onSuccess: () => {
            setLocalPaymentStatus(pendingPaymentStatus);
            setPaymentUpdating(false);
            setInvoiceModal(false);
            setInvoiceFile(null);
            getItemLocker({});
          },
          onError: () => {
            setPaymentUpdating(false);
            toast.error("Failed to update payment status");
          },
        },
      });
    } catch (err) {
      toast.error(err?.data?.message || "Invoice upload failed.");
    } finally {
      setInvoiceUploading(false);
    }
  };
  const [openNote, setOpenNote] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [openViewItem, setOpenViewItem] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const scrollRef = useRef(null);
  const [imageScroll, setImageScroll] = useState({
    canScroll: false,
    atStart: true,
    atEnd: true,
  });
  const hasQuantity = quantity !== undefined && quantity !== null && String(quantity).trim() !== "";

  const updateImageScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const canScroll = el.scrollWidth > el.clientWidth + 1;
    setImageScroll({
      canScroll,
      atStart: el.scrollLeft <= 1,
      atEnd: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1,
    });
  }, []);

  useEffect(() => {
    updateImageScroll();
    const el = scrollRef.current;
    if (!el) return undefined;

    el.addEventListener("scroll", updateImageScroll, { passive: true });
    const observer = new ResizeObserver(updateImageScroll);
    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", updateImageScroll);
      observer.disconnect();
    };
  }, [images, updateImageScroll]);

  const scroll = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir * 160, behavior: "smooth" });
  };
  const handleOpenReviewApprove = () => setOpenReviewApprove(true);
  const handleCloseVirtualSuccess = () => setOpenReviewApprove(false);

  const handleDelete = () => {
    dispatch({
      type: VIRTUAL_LOCKER_DELETE_ITEM,
      payload: { id: itemId, onSuccess: () => getItemLocker({}) },
    });
    setOpenDeleteConfirm(false);
  };

  return (
    <>
      <StyledMainBox>

        {/* ── Dark gradient header: title + status + qty ── */}
        <StyledCardHeader>
          <StyledTitle onClick={() => navigate(`/dashboard/items/${itemId}`)}>{product}</StyledTitle>
          <StyledHeaderRight>
            <span
              style={{
                background: statusConfig[status]?.bg || "#F0F0F0",
                color: statusConfig[status]?.color || "#444",
                fontWeight: 700,
                fontSize: "11px",
                padding: "3px 10px 3px 7px",
                borderRadius: "20px",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                border: `1px solid ${statusConfig[status]?.dot || "#ccc"}`,
              }}
            >
              <span style={{
                width: 7, height: 7,
                borderRadius: "50%",
                background: statusConfig[status]?.dot || "#999",
                display: "inline-block",
                flexShrink: 0,
              }} />
              {formatName(status)}
            </span>
            {hasQuantity && <StyledQuantity>× {quantity}</StyledQuantity>}
          </StyledHeaderRight>
        </StyledCardHeader>

        {/* ── Chip row (shelf / type / note) ── */}
        {path?.pathname !== "/cart" && (
          <StyledBoxFlex pad="true">
            <StyledChipRow>
              {customerDeclineNote && (
                <StyledNoteButton onClick={() => setOpenNote(true)}>
                  View Notes
                </StyledNoteButton>
              )}
              <CustomChip
                isIcon={true}
                icon={rectangle}
                title={chip2Title}
                color={theme.palette.custom.packIconColor}
                background={theme.palette.custom.chipSelfBackground}
              />
            </StyledChipRow>
            {path?.pathname !== "/dashboard/cart" && (
              <Box sx={{ display: "flex", gap: "4px", marginLeft: "auto" }}>
                {isLockerV2 && (
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/dashboard/items/${itemId}`)}
                    aria-label="View item"
                    sx={{
                      color: "#0B2373",
                      background: "#eef3fb",
                      borderRadius: "6px",
                      p: "4px",
                      "&:hover": { background: "#dce8f8" },
                    }}
                  >
                    <VisibilityOutlinedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                )}
                {!hideEdit && (
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/dashboard/locker/item?id=${itemId}`)}
                    sx={{ color: "#0B65B5", background: "#e8f1fb", borderRadius: "6px", p: "4px", "&:hover": { background: "#d0e4f7" } }}
                  >
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                )}
                {!hideDelete && (
                  <IconButton
                    size="small"
                    onClick={() => setOpenDeleteConfirm(true)}
                    sx={{ color: "#CC2200", background: "#FFE8E0", borderRadius: "6px", p: "4px", "&:hover": { background: "#ffd0c8" } }}
                  >
                    <DeleteIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                )}
              </Box>
            )}
          </StyledBoxFlex>
        )}

        {/* ── Info rows ── */}
        <StyledOrderDetailsBox>
          <StyledBox>
            <StyledRowFlex>
              <SenderLabel>Sender ({senderType})</SenderLabel>
              <SenderValue>
                <StyledSpan>{createUser?.name}</StyledSpan>{" "}
                ({createUser?.phone_number})
              </SenderValue>
            </StyledRowFlex>
            <Divider sx={{ borderColor: "#e8f0fb" }} />
            <StyledRowFlex>
              <SenderLabel>Created On</SenderLabel>
              <SenderValue>{createdAt}</SenderValue>
            </StyledRowFlex>
            <Divider sx={{ borderColor: "#e8f0fb" }} />
            <StyledRowFlex>
              <SenderLabel>Exp. Delivery</SenderLabel>
              <SenderValue>{expDelivery || "—"}</SenderValue>
            </StyledRowFlex>
            {orderType === "self_drop" && (
              <>
                <Divider sx={{ borderColor: "#e8f0fb" }} />
                <StyledRowFlex>
                  <SenderLabel>Preferred Drop Date</SenderLabel>
                  <SenderValue>{preferredDropDate || "—"}</SenderValue>
                </StyledRowFlex>
              </>
            )}
            {isPersonalShopper && (
              <>
                <Divider sx={{ borderColor: "#e8f0fb" }} />
                <StyledRowFlex sx={{ alignItems: "center" }}>
                  <SenderLabel>Payment Status</SenderLabel>
                  <SenderValue sx={{ display: "flex", justifyContent: "flex-end" }}>
                    {isPaid || isRejected ? (
                      <span style={{
                        background: isPaid ? "#E0F7EC" : "#FFE8EA",
                        color: isPaid ? "#065F2E" : "#9B0012",
                        fontWeight: 700,
                        fontSize: "11px",
                        padding: "4px 10px 4px 8px",
                        borderRadius: "20px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        border: `1px solid ${isPaid ? "#00C046" : "#FF3850"}`,
                        whiteSpace: "nowrap",
                      }}>
                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: isPaid ? "#00C046" : "#FF3850", display: "inline-block", flexShrink: 0 }} />
                        {isPaid ? "Paid" : localPaymentStatus === "partial_paid" ? "Partial Paid" : "Unpaid"}
                      </span>
                    ) : (
                      <Select
                        size="small"
                        value={localPaymentStatus}
                        onChange={handlePaymentStatusChange}
                        disabled={paymentUpdating}
                        displayEmpty
                        variant="outlined"
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                          minWidth: 120,
                          height: 28,
                          borderRadius: "20px",
                          background: localPaymentStatus === "partial_paid" ? "#FFF3CD" : "#FFE8EA",
                          color: localPaymentStatus === "partial_paid" ? "#856404" : localPaymentStatus === "not_paid" ? "#9B0012" : "#444",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: localPaymentStatus === "partial_paid" ? "#F0A500" : localPaymentStatus === "not_paid" ? "#FF3850" : "#ccc",
                            borderRadius: "20px",
                          },
                          "& .MuiSelect-select": {
                            padding: "0 28px 0 10px",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          },
                          "& .MuiSelect-icon": { right: 6 },
                        }}
                        renderValue={(val) => {
                          const dot = val === "partial_paid" ? "#F0A500" : val === "not_paid" ? "#FF3850" : "#999";
                          const label = val === "partial_paid" ? "Partial Paid" : val === "not_paid" ? "Unpaid" : "Select";
                          return (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                              <span style={{ width: 7, height: 7, borderRadius: "50%", background: dot, display: "inline-block", flexShrink: 0 }} />
                              {label}
                            </span>
                          );
                        }}
                      >
                        <MuiMenuItem value="partial_paid" sx={{ fontSize: "12px" }}>Partial Paid</MuiMenuItem>
                        <MuiMenuItem value="paid" sx={{ fontSize: "12px" }}>Paid</MuiMenuItem>
                        <MuiMenuItem value="not_paid" disabled={localPaymentStatus === "partial_paid"} sx={{ fontSize: "12px" }}>Unpaid</MuiMenuItem>
                      </Select>
                    )}
                  </SenderValue>
                </StyledRowFlex>
              </>
            )}
          </StyledBox>
        </StyledOrderDetailsBox>

        {/* ── Images + action buttons footer ── */}
        {/* {images?.length > 0 && (
          <StyledTypography>Sender has uploaded a few pictures*</StyledTypography>
        )} */}
        <StyledLockerActionBox>
          {images?.length === 0 ? (
            <StyledNoImageBox>
              <StyledNoImageText>No product images</StyledNoImageText>
            </StyledNoImageBox>
          ) : (
          <Box sx={{ position: "relative", flex: 1, minWidth: 0, display: "flex", alignItems: "center" }}>
            {imageScroll.canScroll && !imageScroll.atStart && (
              <IconButton
                onClick={() => scroll(-1)}
                size="small"
                aria-label="Scroll images left"
                sx={{
                  position: "absolute", left: -14, zIndex: 1,
                  background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                  width: 24, height: 24,
                  "&:hover": { background: "#f0f0f0" },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            <StyledImageContainer ref={scrollRef}>
              {images?.length > 0 ? images.map((imgSrc, index) => (
                <Box
                  key={index}
                  component="button"
                  type="button"
                  onClick={() => setPreviewImage(imgSrc)}
                  sx={{
                    flexShrink: 0,
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: "1px solid #d0e4f7",
                    boxShadow: "0 2px 6px rgba(11,101,181,0.1)",
                    p: 0,
                    cursor: "pointer",
                    background: "transparent",
                  }}
                >
                  <img
                    src={imgSrc}
                    alt={`Locker ${index + 1}`}
                    width={64}
                    height={60}
                    loading="lazy"
                    style={{ borderRadius: 8, objectFit: "cover", display: "block" }}
                  />
                </Box>
              )) : null}
            </StyledImageContainer>
            {imageScroll.canScroll && !imageScroll.atEnd && (
              <IconButton
                onClick={() => scroll(1)}
                size="small"
                aria-label="Scroll images right"
                sx={{
                  position: "absolute", right: -14, zIndex: 1,
                  background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                  width: 24, height: 24,
                  "&:hover": { background: "#f0f0f0" },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
          )}

          <StyledBoxStatus>
            {path?.pathname !== "/dashboard/cart" && isPictureReviewPending && (
              <>
                <Tooltip
                  title={isPersonalShopper && !isPaid ? "Payment must be paid to approve" : ""}
                  placement="top"
                  arrow
                  disableHoverListener={!(isPersonalShopper && !isPaid)}
                >
                  <span>
                    <CustomButton
                      padding="7px 15px"
                      fontSize={theme.typography.mini.fontSize}
                      height="29px"
                      btnLabel="Approve"
                      btntype={"primary"}
                      disabled={isPersonalShopper && !isPaid}
                      onClick={() => {
                        setReviewStatus("approved");
                        handleOpenReviewApprove();
                      }}
                    />
                  </span>
                </Tooltip>
                <CustomButton
                  padding="7px 15px"
                  fontSize={theme.typography.mini.fontSize}
                  height="29px"
                  btnLabel="Decline"
                  btntype={"danger"}
                  onClick={() => {
                    setReviewStatus("rejected");
                    handleOpenReviewApprove();
                  }}
                />
              </>
            )}
          </StyledBoxStatus>
        </StyledLockerActionBox>

        {openReviewApprove && (
          <ReviewApprove
            status={reviewStatus}
            getItemLocker={getItemLocker}
            id={itemId}
            images={images}
            open={openReviewApprove}
            handleClose={handleCloseVirtualSuccess}
          />
        )}

        <Modal open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
          <StyledNoteModalBox sx={{ width: 480 }}>
            <StyledNoteModalHeader>
              <StyledNoteModalTitle>Delete Item</StyledNoteModalTitle>
              <IconButton onClick={() => setOpenDeleteConfirm(false)} sx={{ color: "#fff", p: 0 }}>
                <CloseIcon />
              </IconButton>
            </StyledNoteModalHeader>
            <StyledNoteModalContent sx={{ p: "28px 28px 24px" }}>
              <StyledNoteText sx={{ fontSize: "15px", mb: 1 }}>Are you sure you want to delete <strong>{product}</strong>?</StyledNoteText>
              <Box sx={{ display: "flex", gap: "12px", justifyContent: "flex-end", mt: 3 }}>
                <CustomButton
                  padding="10px 32px"
                  height="44px"
                  fontSize={theme.typography.base.fontSize}
                  btnLabel="Cancel"
                  btntype="white"
                  border="1px solid #ccc"
                  color="#333333"
                  onClick={() => setOpenDeleteConfirm(false)}
                />
                <CustomButton
                  padding="10px 32px"
                  height="44px"
                  fontSize={theme.typography.base.fontSize}
                  btnLabel="Delete"
                  btntype="danger"
                  onClick={handleDelete}
                />
              </Box>
            </StyledNoteModalContent>
          </StyledNoteModalBox>
        </Modal>

        <AddNotesModal
          open={openNote}
          handleClose={() => setOpenNote(false)}
          row={{ id: itemId, product_name: product }}
          intakeScope="customer"
          viewOnly
        />

        {/* Invoice Upload Modal */}
        <Modal open={invoiceModal} onClose={invoiceUploading ? undefined : () => { setInvoiceModal(false); setInvoiceFile(null); }}>
          <StyledNoteModalBox sx={{ width: 480 }}>
            <StyledNoteModalHeader>
              <StyledNoteModalTitle>Invoice / Payment Proof</StyledNoteModalTitle>
              <IconButton onClick={() => { setInvoiceModal(false); setInvoiceFile(null); }} sx={{ color: "#fff", p: 0 }} disabled={invoiceUploading}>
                <CloseIcon />
              </IconButton>
            </StyledNoteModalHeader>
            <StyledNoteModalContent sx={{ p: "24px 28px" }}>
              <StyledInvoiceModalDesc>
                Please upload your invoice or payment proof to confirm <strong>{pendingPaymentStatus === "paid" ? "Paid" : "Partial Paid"}</strong> status.
              </StyledInvoiceModalDesc>
              <StyledInvoiceUploadBox
                hasfile={invoiceFile ? 1 : 0}
                onClick={() => document.getElementById("locker-invoice-upload").click()}
              >
                <input
                  id="locker-invoice-upload"
                  type="file"
                  accept="image/*,.pdf,application/pdf"
                  style={{ display: "none" }}
                  onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
                />
                <CloudUploadOutlinedIcon sx={{ fontSize: 36, color: invoiceFile ? "#00C046" : "#126ABC", mb: 1 }} />
                <StyledInvoiceFileName hasfile={invoiceFile ? 1 : 0}>
                  {invoiceFile ? invoiceFile.name : "Click to upload invoice / payment proof"}
                </StyledInvoiceFileName>
                <StyledInvoiceHint>Image or PDF</StyledInvoiceHint>
              </StyledInvoiceUploadBox>
              <StyledInvoiceButtonRow>
                <StyledInvoiceCancelButton
                  variant="outlined"
                  onClick={() => { setInvoiceModal(false); setInvoiceFile(null); }}
                  disabled={invoiceUploading}
                >
                  Cancel
                </StyledInvoiceCancelButton>
                <StyledInvoiceSubmitButton
                  variant="contained"
                  onClick={handleInvoiceSubmit}
                  disabled={invoiceUploading || !invoiceFile}
                >
                  {invoiceUploading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Submit"}
                </StyledInvoiceSubmitButton>
              </StyledInvoiceButtonRow>
            </StyledNoteModalContent>
          </StyledNoteModalBox>
        </Modal>

        {isLockerV2 && (
          <ViewLockerItemModal
            open={openViewItem}
            onClose={() => setOpenViewItem(false)}
            getItemLocker={getItemLocker}
            isLockerInactive={isLockerInactive}
            item={{
              itemId,
              product,
              status,
              quantity,
              orderType,
              senderType,
              createUser,
              createdAt,
              expDelivery,
              preferredDropDate,
              note,
              images,
            }}
          />
        )}

        <Modal
          open={!!previewImage}
          onClose={() => setPreviewImage(null)}
          slots={{ backdrop: Backdrop }}
          slotProps={{ backdrop: { timeout: 300, style: { background: "rgba(10,20,50,0.9)" } } }}
        >
          <Fade in={!!previewImage}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                outline: "none",
              }}
            >
              <Box
                component="img"
                src={previewImage || ""}
                alt="Product full view"
                sx={{
                  maxWidth: "88vw",
                  maxHeight: "82vh",
                  borderRadius: "12px",
                  boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          </Fade>
        </Modal>
      </StyledMainBox>
    </>
  );
};
export default CustomLocker;
